/* eslint-disable no-console */
import React, { useContext } from 'react';
import { Formik } from 'formik';
import { connect } from 'react-redux';
import * as Yup from 'yup';
import isEqual from 'lodash.isequal';
import { ZipCodeInput, Input } from '@carvana/showroom-forms';
import {
  AnalyticsContext,
  ContextType as AnalyticsContextType
} from '@carvana/eventable-clickstream';
import type Address from '../../../contracts/Address';
import type { RootState } from '../../../store/types';
import {
  EditAddressFormWrapper,
  CityFormInput,
  UsStatesSelectWrapper
} from './EditAddressForm.styles';
import { Row, Column } from '../../../shared/components/Layout';
import ContactsApi from '../../../shared/apis/ContactsApi';
import FormButtons from '../../../shared/components/FormButtons/FormButtons';
import { personalDetailsOperations, personalDetailsSelectors } from '../ducks';
import formValidationErrorMessages from '../../../shared/constants/form/formValidationErrorMessages';
import '@carvana/showroom-forms/Input/index.css';

const {
  GENERIC: { REQUIRED }
} = formValidationErrorMessages;

const validationSchema = Yup.object().shape({
  street: Yup.string().when('isOnlyDeliveryZip', {
    is: false,
    then: Yup.string().required(REQUIRED),
    otherwise: Yup.string().notRequired()
  }),
  city: Yup.string().when('isOnlyDeliveryZip', {
    is: false,
    then: Yup.string().required(REQUIRED),
    otherwise: Yup.string().notRequired()
  }),
  state: Yup.string().when('isOnlyDeliveryZip', {
    is: false,
    then: Yup.string().required(REQUIRED),
    otherwise: Yup.string().notRequired()
  }),
  zip5: Yup.string()
    .required('Zip code is required')
    .min(5, 'Please enter a valid zip code')
    .max(5, 'Must be no more than 5 numbers')
});

type AddressFormValues = {
  city: string;
  isOnlyDeliveryZip: boolean;
  state: string;
  street: string;
  street2: string;
  zip5: string;
};

type OnSubmitArgs = {
  initialValues: AddressFormValues;
  isOrderPlaced: boolean;
  requestKind: string;
  sectionName: string;
  trackEvent: AnalyticsContextType['trackEvent'];
  updateAddress: (
    address: Record<string, unknown>,
    sectionName: string,
    trackEvent: (...args: any[]) => void,
    isOrderPlaced: boolean
  ) => void;
  values: AddressFormValues;
};

const onSubmit = ({
  values,
  requestKind,
  initialValues,
  sectionName,
  updateAddress,
  trackEvent,
  isOrderPlaced
}: OnSubmitArgs) => {
  const shouldSendOnlyZip =
    requestKind === 'DeliveryOnly' &&
    !initialValues.street &&
    !initialValues.street2 &&
    !initialValues.city &&
    !initialValues.state &&
    initialValues.zip5 &&
    values.zip5 !== initialValues.zip5;
  const updatedAddress = {
    kind: requestKind,
    synchronizeDelivery: false,
    address: shouldSendOnlyZip
      ? { zip5: values.zip5 }
      : {
          city: values.city,
          state: values.state,
          street: values.street,
          street2: values.street2,
          zip5: values.zip5
        }
  };
  updateAddress(updatedAddress, sectionName, trackEvent, isOrderPlaced);
};

type OwnProps = {
  address?: Address;
  requestKind: string;
  sectionName: string;
};

type StateProps = {
  isOrderPlaced: boolean;
  isSubmitting: boolean;
};

type DispatchProps = {
  updateAddress: (
    address: Record<string, unknown>,
    sectionName: string,
    trackEvent: (...args: any[]) => void,
    isOrderPlaced: boolean
  ) => void;
};

const EditAddressForm = (props: OwnProps & StateProps & DispatchProps) => {
  // eslint-disable-next-line react/destructuring-assignment
  const { address } = props.address;
  const { updateAddress, requestKind, sectionName, isSubmitting, isOrderPlaced } = props;
  const isOnlyDeliveryZip = !address.street && requestKind !== 'MailOnly';
  const initialValues = {
    street: address.street,
    street2: address.street2,
    city: address.city,
    state: address.state,
    zip5: address.zipCode.zip5,
    isOnlyDeliveryZip
  };
  const { trackEvent } = useContext(AnalyticsContext);

  return (
    <Formik
      validationSchema={validationSchema}
      initialValues={initialValues}
      onSubmit={async (values, actions) => {
        try {
          await ContactsApi.validateZip5(values.zip5);
        } catch (e) {
          actions.setErrors({ zip5: 'Invalid Zip Code' });
          return false;
        }

        return onSubmit({
          values,
          requestKind,
          initialValues,
          sectionName,
          updateAddress,
          trackEvent,
          isOrderPlaced
        });
      }}
      render={({
        touched,
        handleSubmit,
        handleChange,
        handleBlur,
        values,
        errors,
        setFieldValue
      }) => {
        function sanitizeZip5(event: React.ChangeEvent<HTMLInputElement>) {
          const {
            target: { name: field, value }
          } = event;
          const isInputNumber = /^[0-9]+$/.test(value);
          const shouldSetFieldValue = (isInputNumber && value.length <= 5) || value === '';

          if (shouldSetFieldValue) setFieldValue(field, value);
        }

        const hasError = !isEqual(errors, {});
        const shouldDisableSubmit = isSubmitting || isEqual(initialValues, values) || hasError;

        return (
          <EditAddressFormWrapper onSubmit={handleSubmit}>
            {!isOnlyDeliveryZip && (
              <>
                <Row>
                  <Column>
                    <Input
                      onChange={handleChange}
                      onBlur={handleBlur}
                      id="street"
                      type="text"
                      name="street"
                      label="Street Address"
                      touched={touched.street}
                      value={values.street}
                      error={errors.street}
                    />
                  </Column>
                </Row>

                <Row>
                  <Column>
                    <Input
                      onChange={handleChange}
                      onBlur={handleBlur}
                      id="street2"
                      type="text"
                      name="street2"
                      label="Street Address 2"
                      touched={touched.street2}
                      value={values.street2}
                      error={errors.street2}
                    />
                  </Column>
                </Row>

                <Row>
                  <Column>
                    <CityFormInput
                      onChange={handleChange}
                      onBlur={handleBlur}
                      id="city"
                      type="text"
                      name="city"
                      label="City"
                      touched={touched.city}
                      value={values.city}
                      error={errors.city}
                    />
                  </Column>

                  <Column>
                    <UsStatesSelectWrapper
                      id="us-states-select"
                      onChange={(event) => setFieldValue('state', event.target.value)}
                      value={values.state}
                    />
                  </Column>
                </Row>
              </>
            )}

            <Row>
              <Column>
                <ZipCodeInput
                  onChange={sanitizeZip5}
                  onBlur={handleBlur}
                  id="zip5"
                  name="zip5"
                  touched={touched.zip5}
                  value={values.zip5}
                  error={errors.zip5}
                />
              </Column>
            </Row>

            <FormButtons isSubmitting={isSubmitting} isSubmitDisabled={shouldDisableSubmit} />
          </EditAddressFormWrapper>
        );
      }}
    />
  );
};

const mapDispatchToProps = {
  updateAddress: personalDetailsOperations.updateAddress
};

const mapStateToProps = (state: RootState) => {
  return {
    isOrderPlaced: personalDetailsSelectors.getIsOrderPlaced(state),
    isSubmitting: personalDetailsSelectors.getUpdateAddressIsLoading(state)
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(EditAddressForm);
